import React, { useState } from 'react';
import { ReactElement } from 'react';
import {
  NotificationMessage,
  Sidebar,
  SidebarSection,
  toastError,
  toastSuccess,
  NullState,
} from 'bench-internal-theme';
import { showServerError } from 'bk-web-app';
import { WrappedSpinner } from 'spinner';
import { useV2Query } from 'utils';
import {
  ClientRulesLayout,
  ContractorListLayout,
  ContractorInput,
  FormFields,
  InlineInputLayout,
  AddContractorWrapper,
  AddButton,
  AddContractorErrorWrapper,
  AddContractorError,
  HeaderRow,
  ContractorList,
  ContractorListItem,
  IconContainer,
} from './ManageContractorSidebar.styles';
import { colors } from 'bench-style-constants';
import Icon from 'primitive-components/Icon';
import useV2Mutation from 'utils/useV2Mutation';
import { CREATE_OR_UPDATE_CONTRACTOR } from 'general-ledger/components/report-1099-form/graphql/mutations';
import {
  DELETE_CONTRACTOR,
  GET_CONTRACTOR_LIST_WITH_INSTANCES,
  getJournalEntries1099RefetchQuery,
} from '../transactions-1099-container/graphql';
import DeleteContractorConfirmationModal from '../delete-contractor-confirmation-modal/DeleteContractorConfirmationModal';
import { openModal } from 'modal-core-components';
import EditContractorSidebar from '../edit-contractor-sidebar/EditContractorSidebar';
import { Contractor } from 'general-ledger/types';

type ManageContractorSidebarProps = {
  openSidebar: (ReactElement) => void;
  clientId: string;
  dismiss: () => void;
};

const ManageContractorSidebar = ({
  openSidebar,
  clientId,
  dismiss,
}: ManageContractorSidebarProps): ReactElement => {
  const [contractorName, setContractorName] = useState('');
  const [showContractorError, setShowContractorError] = useState(false);

  // Mutation for Create or Update Contractor
  const [createorUpdateContractor, { loading: createorUpdateContractorLoading }] = useV2Mutation(
    CREATE_OR_UPDATE_CONTRACTOR,
    {
      onError: error => {
        toastError(
          'An error occurred creating or updating this contractor',
          showServerError(error),
        );
      },
      onCompleted: () => {
        toastSuccess('Contractor Created / Updated', 'Your request was successful.');
        setContractorName('');
      },
    },
  );

  // Query to fetch Contractor List
  const { data, loading, error } = useV2Query(GET_CONTRACTOR_LIST_WITH_INSTANCES, {
    variables: { clientId },
    skip: !clientId,
  });

  // Mutation to Delete Contractor
  const [deleteContractor] = useV2Mutation(DELETE_CONTRACTOR, {
    onError: error => {
      toastError('An error occurred deleting this contractor', showServerError(error));
    },
    onCompleted: () => {
      toastSuccess('Contractor Deleted', 'You have successfully deleted a contractor.');
    },
  });

  if (loading) {
    return (
      <Sidebar dismiss={dismiss} headerContent="">
        <WrappedSpinner />
      </Sidebar>
    );
  }
  if (error) {
    return (
      <Sidebar dismiss={dismiss} headerContent="Error">
        <SidebarSection>
          <NotificationMessage data-test-id="contractor-list-error" styleType="error">
            There was a problem fetching the contractors for this client.
          </NotificationMessage>
        </SidebarSection>
      </Sidebar>
    );
  }

  if (!data) {
    return (
      <NullState
        bigEmoji="🤩"
        description="Great job keeping tidy books."
        title="You have no contractor listed for the client!"
      />
    );
  }

  const handleCreateorUpdateContractor = ({ id, name = contractorName }) => {
    if (!clientId) return Promise.resolve();
    return createorUpdateContractor({
      variables: {
        input: {
          clientId: parseInt(clientId),
          id,
          name,
        },
      },
      refetchQueries: [
        {
          query: GET_CONTRACTOR_LIST_WITH_INSTANCES,
          variables: { clientId },
          context: { useV2: true },
        },
        ...getJournalEntries1099RefetchQuery(clientId),
      ],
    });
  };

  const handleDeleteContractor = (contractor: Contractor) => {
    if (!clientId || !contractor.id) return;
    return deleteContractor({
      variables: {
        id: contractor.id,
      },
      refetchQueries: [
        {
          query: GET_CONTRACTOR_LIST_WITH_INSTANCES,
          variables: { clientId },
          context: { useV2: true },
        },
        ...getJournalEntries1099RefetchQuery(clientId),
      ],
    });
  };

  const handleContractorNameChange = event => {
    setContractorName(event.target.value);
    const contractorNames = data?.contractors.map(c => c.name.toLowerCase());
    setShowContractorError(contractorNames.includes(event.target.value.toLowerCase()));
  };

  const openDeleteContractorModal = contractor => {
    openModal(closeModal => (
      <DeleteContractorConfirmationModal
        title="Submission confirmation"
        description={`You are about to delete ${contractor.name}. There ${
          contractor.transactionCount === 1 ? 'is' : 'are'
        } ${contractor.transactionCount} ${
          contractor.transactionCount === 1 ? 'transaction' : 'transactions'
        } that are associated to it. Ensure those are re-assigned prior to deleting it. This action isn’t reversible.`}
        closeModal={closeModal}
        onConfirm={() => handleDeleteContractor(contractor)}
      />
    ));
  };

  const openEditContractorSidebar = (clientId, contractorData, contractorList) =>
    openSidebar(
      <EditContractorSidebar
        handleCreateorUpdateContractor={handleCreateorUpdateContractor}
        contractorData={contractorData}
        contractorList={contractorList}
        dismiss={() =>
          openSidebar(
            <ManageContractorSidebar
              openSidebar={openSidebar}
              clientId={clientId}
              dismiss={dismiss}
            />,
          )
        }
      />,
    );

  return (
    <Sidebar dismiss={dismiss} headerContent="Manage Contractors">
      <ClientRulesLayout>
        <FormFields>
          <SidebarSection hasBorderBottom>
            <ContractorListLayout>
              <HeaderRow>
                <div>CONTRACTOR</div>
                <div>INSTANCES</div>
              </HeaderRow>
              <ContractorList>
                {data?.contractors.map(contractor => (
                  <ContractorListItem key={contractor.id}>
                    <div>{contractor.name}</div>
                    <div>{contractor.transactionCount}</div>
                    <IconContainer>
                      <Icon
                        cursor="pointer"
                        color={colors.cloud}
                        onClick={() =>
                          openEditContractorSidebar(clientId, contractor, data?.contractors || [])
                        }
                        icon="pencil"
                        aria-label="Edit Contractor"
                        size="small"
                      />
                      <Icon
                        cursor="pointer"
                        color={colors.cloud}
                        onClick={() => openDeleteContractorModal(contractor)}
                        icon="trash"
                        aria-label="Delete Contractor"
                        size="small"
                      />
                    </IconContainer>
                  </ContractorListItem>
                ))}
              </ContractorList>
            </ContractorListLayout>
          </SidebarSection>
          <SidebarSection title="New Contractor" hasBorderBottom background={colors.pearl}>
            <InlineInputLayout>
              <AddContractorWrapper>
                <ContractorInput
                  data-test-id="contractor-name"
                  value={contractorName}
                  type="text"
                  onChange={handleContractorNameChange}
                  placeholder="Contractor name"
                  color={showContractorError ? '#d6382c' : undefined}
                />
                <AddButton
                  onClick={handleCreateorUpdateContractor}
                  aria-label="Add a Contractor"
                  type="submit"
                  disabled={
                    createorUpdateContractorLoading ||
                    !contractorName ||
                    showContractorError ||
                    !clientId
                  }
                >
                  ADD
                </AddButton>
              </AddContractorWrapper>
              <AddContractorErrorWrapper>
                {showContractorError && (
                  <AddContractorError aria-label="Add a Contractor Error">
                    You already have an entry with this name
                  </AddContractorError>
                )}
              </AddContractorErrorWrapper>
            </InlineInputLayout>
          </SidebarSection>
        </FormFields>
      </ClientRulesLayout>
    </Sidebar>
  );
};

export default ManageContractorSidebar;
